import { useEffect, useState } from 'react';

const DEFAULT_VIEW = 'grid';
const STORAGE_KEY = 'productViewPreference';
const DEFAULT_ALLOWED_VIEWS = ['grid'];

const normalizeAllowedViews = (allowedViews, fallback) => {
  const normalized = Array.isArray(allowedViews)
    ? allowedViews.map((item) => String(item || '').trim()).filter(Boolean)
    : [];
  const unique = normalized.filter((item, index) => normalized.indexOf(item) === index);
  if (!unique.length) {
    return DEFAULT_ALLOWED_VIEWS.includes(fallback)
      ? DEFAULT_ALLOWED_VIEWS
      : [...DEFAULT_ALLOWED_VIEWS, fallback];
  }
  return unique;
};

const normalizeView = (value, fallback, allowedViews) =>
  allowedViews.includes(value) ? value : fallback;

const readStoredView = (storageKey, fallback, allowedViews) => {
  if (typeof window === 'undefined') return fallback;
  try {
    return normalizeView(window.localStorage.getItem(storageKey), fallback, allowedViews);
  } catch (error) {
    return fallback;
  }
};

const useProductViewPreference = ({
  storageKey = STORAGE_KEY,
  defaultView = DEFAULT_VIEW,
  allowedViews = DEFAULT_ALLOWED_VIEWS,
} = {}) => {
  const resolvedAllowedViews = normalizeAllowedViews(allowedViews, defaultView);
  const resolvedDefaultView = normalizeView(defaultView, resolvedAllowedViews[0], resolvedAllowedViews);
  const [view, setViewState] = useState(() => readStoredView(storageKey, resolvedDefaultView, resolvedAllowedViews));

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(storageKey, view);
    } catch (error) {
      // Ignore storage errors (private mode, blocked storage, etc.)
    }
  }, [storageKey, view]);

  useEffect(() => {
    setViewState((current) => normalizeView(current, resolvedDefaultView, resolvedAllowedViews));
  }, [resolvedAllowedViews, resolvedDefaultView]);

  const setView = (nextValue) => {
    setViewState((current) => {
      const resolvedValue = typeof nextValue === 'function' ? nextValue(current) : nextValue;
      return normalizeView(resolvedValue, resolvedDefaultView, resolvedAllowedViews);
    });
  };

  return [view, setView];
};

export default useProductViewPreference;
